import type { ComponentProps, CSSProperties } from 'react';

import { cn } from '../cn';

/*
 * Copied from ui/icons rather than imported: that barrel brings every icon in
 * the app and its SCSS modules with it, and some of its icons call useId, which
 * a server component cannot do. Every icon here that names ui/icons was copied
 * for that reason. Keep the internal opacities; that layering is the drawing.
 */
type IconProps = ComponentProps<'svg'>;

export const CardIcon = ({ className, ...props }: IconProps) => (
  <svg
    viewBox='0 0 28 28'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden
    className={cn('size-6', className)}
    {...props}>
    <rect
      x='1.5'
      y='6.5'
      width='25'
      height='16'
      rx='2.5'
      fill='currentColor'
      fillOpacity='0.125'
      stroke='currentColor'
    />
    <rect x='1' y='10' width='26' height='5' fill='currentColor' />
  </svg>
);

/* The viewBox is cropped to the mark's own ink rather than kept at the export's
   0 0 28 28, which centred it in four units of air on the left and three on the
   right. Beside a title at a shared gap that read as the bank standing further
   off its words than the marks next to it. */
export const BankIcon = ({ className, ...props }: IconProps) => (
  <svg
    viewBox='4 3.4 21 20.6'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden
    className={cn('size-6', className)}
    {...props}>
    <path
      d='M12.8906 3.7396C13.5624 3.29173 14.4376 3.29173 15.1094 3.7396L23.2519 9.16795C24.0752 9.71678 23.6866 11 22.6972 11H5.30278C4.31337 11 3.92484 9.71677 4.74808 9.16795L12.8906 3.7396Z'
      fill='currentColor'
    />
    {/* The columns are half strength in the original, and that layering is the
        drawing: at full they close the gaps and the mark reads as a block. */}
    {[7, 11, 15, 19].map(x => (
      <rect key={x} x={x} y='12' width='2' height='6' fill='currentColor' fillOpacity='0.5' />
    ))}
    <rect x='5' y='19' width='18' height='2' rx='1' fill='currentColor' />
    <rect x='4' y='22' width='21' height='2' rx='1' fill='currentColor' />
  </svg>
);

/**
 * Drawn here rather than copied from somewhere: nothing in either icon set
 * carries a calendar, and a button that books time wants one.
 *
 * Built like the marks beside it. The viewBox is cropped to the ink, so a
 * shared height reads as a shared size; the sheet is half strength, because at
 * full it closes on the head band and the whole thing reads as one block; and
 * the hangers are drawn before the head so it covers their feet.
 */
export const CalendarIcon = ({ className, ...props }: IconProps) => (
  <svg
    viewBox='1 0 22 23'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden
    className={cn('size-6', className)}
    {...props}>
    <rect x='6' y='0' width='2.5' height='6' rx='1.25' fill='currentColor' />
    <rect x='15.5' y='0' width='2.5' height='6' rx='1.25' fill='currentColor' />
    <rect x='1' y='3' width='22' height='20' rx='4' fill='currentColor' fillOpacity='0.5' />
    <path d='M1 7a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4v2H1V7Z' fill='currentColor' />
    {/* One row of days, not a grid. At the size a button draws this, a second
        row is three more marks under two pixels each. */}
    {[5.5, 10.75, 16].map(x => (
      <rect key={x} x={x} y='13.5' width='2.5' height='2.5' rx='0.75' fill='currentColor' />
    ))}
  </svg>
);

/**
 * Drawn here rather than copied from somewhere: nothing in either icon set
 * carries a calculator, and the two pages that work a number out want one.
 *
 * Stroked rather than filled, to match the house in the nav, which is line art.
 * Same weight and the same round caps: a 3 stroke in a 36 square, so the two
 * read as one set at one size.
 *
 * The viewBox is cropped to the painted ink, stroke included, which is what
 * makes a height class the whole size. The keys are discs rather than short
 * strokes, at the stroke's own width, so they carry the same weight as the
 * lines without being lines.
 *
 * The body is 24 by 30 rather than the 20 by 32 it started at. Narrower than
 * this and with two columns of keys it reads as a phone, earpiece and all;
 * three columns is what makes it a keypad.
 */
export const CalculatorIcon = ({ className, ...props }: IconProps) => (
  <svg
    viewBox='4.5 1.5 27 33'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden
    className={cn('h-6 w-auto', className)}
    {...props}>
    <rect
      x='6'
      y='3'
      width='24'
      height='30'
      rx='4'
      stroke='currentColor'
      strokeWidth='3'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    {/* The display. One line rather than a second rounded rect: at the size a
        nav draws this, a stroked box inside a stroked box closes up. */}
    <path d='M12 11.5h12' stroke='currentColor' strokeWidth='3' strokeLinecap='round' />
    {[21, 27].map(y => [12, 18, 24].map(x => <circle key={`${x}-${y}`} cx={x} cy={y} r='1.5' fill='currentColor' />))}
  </svg>
);

/**
 * A ring, stroked rather than punched out of a disc, so `--ring` can set how
 * thick it draws. The export was a filled donut at an outer 16 and an inner 11;
 * a 5 stroke on a 13.5 radius is that same shape, and 5 is the default here.
 *
 * The radius carries the knob too, at half of it. The ring has to grow inward
 * only: a stroke widens either side of its path, so holding the radius would
 * push the outer edge past the 32 viewBox and the browser would clip it flat.
 *
 * Both are in `style` rather than as presentation attributes, because a `var()`
 * in an attribute is not substituted. `px` inside the viewBox is a user unit.
 */
export const CircleIcon = ({ className, ...props }: IconProps) => (
  <svg
    viewBox='0 0 32 32'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden
    className={cn('size-6', className)}
    {...props}>
    <circle
      cx='16'
      cy='16'
      stroke='currentColor'
      /* Cast because CSSProperties has no `r`; it is an SVG geometry property
         and only reaches the cascade as CSS. */
      style={{ r: 'calc(16px - var(--ring, 5px) / 2)', strokeWidth: 'var(--ring, 5px)' } as CSSProperties}
    />
  </svg>
);

export const AllocationIcon = ({ className, ...props }: IconProps) => (
  <svg
    viewBox='0 0 36 36'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden
    className={cn('size-6', className)}
    {...props}>
    <path
      opacity='0.25'
      d='M31.9996 18C31.9996 25.732 25.7316 32 17.9996 32C10.2676 32 3.9996 25.732 3.9996 18C3.9996 10.268 10.2676 3.99997 17.9996 3.99997C25.7316 3.99997 31.9996 10.268 31.9996 18ZM9.5996 18C9.5996 22.6392 13.3604 26.4 17.9996 26.4C22.6388 26.4 26.3996 22.6392 26.3996 18C26.3996 13.3608 22.6388 9.59997 17.9996 9.59997C13.3604 9.59997 9.5996 13.3608 9.5996 18Z'
      fill='currentColor'
    />
    <path
      opacity='0.5'
      d='M17.9989 3.99988C15.2299 3.99988 12.5232 4.82096 10.2209 6.3593C7.91861 7.89764 6.1242 10.0841 5.06457 12.6423C4.00495 15.2005 3.7277 18.0154 4.26789 20.7311C4.80808 23.4469 6.14146 25.9414 8.09939 27.8994C10.0573 29.8573 12.5519 31.1907 15.2676 31.7309C17.9834 32.2711 20.7983 31.9938 23.3565 30.9342C25.9146 29.8746 28.1011 28.0801 29.6395 25.7779C31.1778 23.4756 31.9989 20.7688 31.9989 17.9999L26.3989 17.9999C26.3989 19.6612 25.9062 21.2853 24.9832 22.6667C24.0602 24.048 22.7483 25.1247 21.2134 25.7605C19.6785 26.3962 17.9896 26.5626 16.3601 26.2385C14.7307 25.9144 13.2339 25.1143 12.0592 23.9396C10.8844 22.7648 10.0844 21.2681 9.76029 19.6386C9.43617 18.0092 9.60252 16.3202 10.2383 14.7853C10.8741 13.2504 11.9507 11.9385 13.3321 11.0155C14.7135 10.0925 16.3375 9.59988 17.9989 9.59988L17.9989 3.99988Z'
      fill='currentColor'
    />
    <path
      d='M17.9989 3.99988C15.6966 3.99988 13.4298 4.56768 11.3993 5.65298C9.36887 6.73828 7.63741 8.30759 6.35831 10.2219C5.07921 12.1362 4.29197 14.3364 4.0663 16.6276C3.84063 18.9189 4.18351 21.2304 5.06457 23.3574C5.94563 25.4845 7.33767 27.3615 9.11738 28.822C10.8971 30.2826 13.0095 31.2817 15.2676 31.7309C17.5257 32.18 19.8597 32.0654 22.0629 31.397C24.266 30.7287 26.2704 29.5274 27.8984 27.8994L23.9386 23.9396C22.9618 24.9164 21.7592 25.6372 20.4373 26.0382C19.1154 26.4392 17.715 26.508 16.3601 26.2385C15.0053 25.969 13.7378 25.3695 12.67 24.4932C11.6022 23.6168 10.7669 22.4907 10.2383 21.2144C9.70966 19.9382 9.50393 18.5513 9.63933 17.1765C9.77473 15.8018 10.2471 14.4817 11.0145 13.3331C11.782 12.1845 12.8209 11.2429 14.0392 10.5917C15.2574 9.94056 16.6175 9.59988 17.9989 9.59988L17.9989 3.99988Z'
      fill='currentColor'
    />
  </svg>
);

export const ProfileIcon = ({ className, ...props }: IconProps) => (
  <svg
    viewBox='0 0 32 32'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden
    className={cn('size-6', className)}
    {...props}>
    <rect x='1' y='1' width='30' height='30' rx='15' stroke='currentColor' strokeWidth='2' />
    <path
      d='M16 1C24.8366 1 32 8.16344 32 17C32 22.0524 29.6574 26.557 26 29.4893V27C25.9999 23.7965 24.4916 20.9463 22.1484 19.1162C20.681 20.8779 18.4719 22 16 22C13.5278 22 11.3181 20.8781 9.85059 19.1162C7.50779 20.9463 6.00013 23.7968 6 27V29.4893C2.34257 26.557 0 22.0524 0 17C0 8.16344 7.16344 1 16 1ZM16 8C12.6863 8 10 10.6863 10 14C10 17.3137 12.6863 20 16 20C19.3137 20 22 17.3137 22 14C22 10.6863 19.3137 8 16 8Z'
      fill='currentColor'
    />
  </svg>
);
